import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { PRISMAX_BACKEND_URL } from '../../configs/config';
import styles from './BetaAccessGate.module.css';


export default function BetaAccessGate() {
    const [isVerified, setIsVerified] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [sessionExpired, setSessionExpired] = useState(false);
    const expiryTimerRef = useRef(null);

    // Validate the betaAccessToken value in localStorage
    const checkExistingSession = async () => {
        const betaToken = localStorage.getItem('betaAccessToken');
        if (!betaToken) {
            setIsVerified(false);
            setIsLoading(false);
            return;
        }

        try {
            const res = await fetch(`${PRISMAX_BACKEND_URL}/auth/beta/validate-token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    beta_token: betaToken
                })
            });

            if (res.ok) {
                setIsVerified(true);
                setSessionExpired(false);
                _scheduleExpiryTimer(betaToken);
            } else {
                localStorage.removeItem('betaAccessToken');
                setIsVerified(false);
            }
        } catch {
            setIsVerified(false);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper function to detect when the JWT expires and re-prompt the user for the access code
    const _scheduleExpiryTimer = (token) => {
        if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);

        try {
            const exp = JSON.parse(atob(token.split('.')[1])).exp;
            const msUntilExpiry = exp * 1000 - Date.now();
            if (msUntilExpiry <= 0) return; // already expired; server check will catch it

            expiryTimerRef.current = setTimeout(() => {
                localStorage.removeItem('betaAccessToken');
                setIsVerified(false);
                setSessionExpired(true);
            }, msUntilExpiry);
        } catch {
            // Malformed token — leave expiry handling to the server
        }
    };

    useEffect(() => {
        checkExistingSession();
        return () => {
            if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Focus input when the gate becomes visible
    useEffect(() => {
        if (!isVerified && !isLoading) {
            document.getElementById('beta-access-input')?.focus();
        }
    }, [isVerified, isLoading]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() || submitting) return;

        setSubmitting(true);
        setError('');

        try {
            const res = await fetch(`${PRISMAX_BACKEND_URL}/auth/beta/verify-access-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ access_code: inputValue.trim() }),
            });

            const data = await res.json();

            if (res.ok && data.success && data.token) {
                localStorage.setItem('betaAccessToken', data.token);
                setIsVerified(true);
                setSessionExpired(false);
                _scheduleExpiryTimer(data.token);
            } else {
                setError(data.error || 'Invalid access code.');
            }
        } catch {
            setError('Unable to reach the server. Please try again.');
        } finally {
            setSubmitting(false);
            setInputValue('');
        }
    };

    // Block the app with a spinner so content does not show 
    // before the JWT auth check completes on initial page load
    if (isLoading) {
        return ReactDOM.createPortal(
            <div className={styles.backdrop}>
                <div className={styles.spinner} aria-label="Checking session…" />
            </div>,
            document.body,
        );
    }

    // Verification has passed - do not render the modal and allow the user 
    // to use the beta app regularly
    if (isVerified) return null;

    // Verification has not passed - access code is required
    return ReactDOM.createPortal(
        <div
            className={styles.backdrop}
            role="dialog"
            aria-modal="true"
            aria-label="Beta access required"
        >
            <div className={styles.card}>
                <div className={styles.header}>
                    <div className={styles.badge}>BETA</div>
                    <h1 className={styles.title}>Restricted Access</h1>
                    <p className={styles.subtitle}>
                        {
                            sessionExpired ? (
                                <>
                                    Your session has expired. Please re-enter the code for beta access.
                                </>
                            ) : (
                                <>
                                    This environment is for internal use only.<br />
                                    Enter your access code to continue.
                                </>
                            )
                        }
                     
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className={`${styles.form}`}
                    noValidate
                >
                    <div className={styles.inputWrapper}>
                        <input
                            id="beta-access-input"
                            className={`${styles.input} ${error ? styles.inputError : ''}`}
                            type="password"
                            placeholder="Access code"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            autoComplete="off"
                            spellCheck={false}
                            disabled={submitting}
                        />
                    </div>

                    {error && (
                        <p className={styles.errorMsg} role="alert">
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        className={styles.btn}
                        disabled={submitting || !inputValue.trim()}
                    >
                        {submitting ? <span className={styles.btnSpinner} /> : 'Continue'}
                    </button>
                </form>
            </div>
        </div>,
        document.body,
    );
}